import {
  GetProposalQuery,
  ProposalSelectionMode,
  SetProposalGroupChildSelectedInput,
} from '@/gql/graphql';

import {
  ProposalGroupChild,
  isProposalLineItemId,
  produceProposalGroupChildren,
} from './proposalSectionTree';

const isTarget = (
  child: ProposalGroupChild,
  input: SetProposalGroupChildSelectedInput
): boolean =>
  child.__typename === 'ProposalGroupLineItem'
    ? input.child.proposalLineItemId != null &&
      isProposalLineItemId(
        child.proposalLineItem,
        input.child.proposalLineItemId
      )
    : 'key' in child && child.key === input.child.groupKey;

/**
 * Mirrors the server's `setProposalGroupChildSelected` rules: under a
 * MULTI_CHOICE parent, selecting one child deselects its siblings. A no-op unless
 * the target is among these children, so sibling deselection stays scoped to the
 * group that owns the target.
 */
const applySelectionToChildren = (
  children: ProposalGroupChild[],
  parentSelectionMode: ProposalSelectionMode,
  input: SetProposalGroupChildSelectedInput
): void => {
  if (!children.some((child) => isTarget(child, input))) return;

  const deselectSiblings =
    parentSelectionMode === 'MULTI_CHOICE' && input.selected;

  for (const child of children) {
    // The deepest nesting level emits fieldless ProposalItemGroup stubs with no
    // selection state; skip anything that can't carry `selected`.
    if (!('selected' in child)) continue;
    if (isTarget(child, input)) {
      child.selected = input.selected;
    } else if (deselectSiblings) {
      child.selected = false;
    }
  }
};

/** Updates whichever group directly contains the targeted child. */
export const applyProposalGroupChildSelection = (
  data: GetProposalQuery,
  input: SetProposalGroupChildSelectedInput
): GetProposalQuery =>
  produceProposalGroupChildren(data, (children, selectionMode) =>
    applySelectionToChildren(children, selectionMode, input)
  );
